
import { takeUntil, mergeMap, filter, map } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { selectInterviewDetailsById, selectInterviewDetailsLoading, IInterviewDetails } from '../../store/interviews/state';
import { Observable, Subject, of, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import { GetInterviewDetailsAction, CreateInterviewAction, UpdateInterviewAction, CreateUpdateInterviewRequestAction } from '../../store/interviews/actions';
import { ITimePicker } from '../common/combo-picker/combo-picker.component';
import { ConfigService } from '../../config/config.service';
import { selectCurrentUserRole, selectUserIsLoading } from '../../store/user/state';
import { Roles } from '../../models/enums';
import { cloneDeep } from 'lodash';
import * as moment from 'moment';
import { BaseComponent } from '../base-component/base.component';
import { GetInterviewLocationsAction } from '../../store/location/actions';
import { ValidationService } from '../../services/validation.service';
import { selectLanguagesByIds, selectLanguagesLoading } from '../../store/bpp/state';
import { selectLocationById, selectLocationLoading } from '../../store/location/state';
import { EventStatus, RequestStatus } from '../../services/bpp.webapi/client';
import { IDentity, FxRequest } from '../../store/fx-request/state';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmWithdrawPopupComponent } from '../common/withdraw-request/confirm-withdraw-popup.component';
import { getLastSavedText, isGuidNotEmpty } from '../../helpers/common';
import { InterviewRequestMapper } from '../../mappers/interview-request.mapper';
import { isNonEmptyNotes } from '../../helpers/league.helper';
import { RejectConfirmComponent } from '../common/reject-confirm/reject-confirm.component';
import { getMinDate, getMaxDate } from '../../helpers/date-helper';
import { InterviewsState } from '../../store/interviews/state';

export enum EInterviewView {
  Undefined = 0,
  LeagueCreate,
  LeagueUpdate,
  BroadcasterRead,
  BroadcasterEdit
}

@Component({
  selector: 'app-interview-details',
  templateUrl: './interview-details.component.html',
  styleUrls: ['./interview-details.component.scss']
})
export class InterviewDetailsComponent extends BaseComponent implements OnInit {
  details$: Observable<IInterviewDetails>;
  isLoading$: Observable<boolean>;
  data: IInterviewDetails;
  broadcasterRequest: FxRequest;
  original: IInterviewDetails;
  lastSaved: string;
  minStart: Subject<ITimePicker> = new Subject();
  maxStart: Subject<ITimePicker> = new Subject();
  minEnd: Subject<ITimePicker> = new Subject();
  maxEnd: Subject<ITimePicker> = new Subject();
  minutesStep = 5;
  isDebug: boolean;
  editMode = false;
  EInterviewView = EInterviewView;
  interviewView: EInterviewView;
  isPastEvent = false;
  minDate: { year: number; month: number; day: number; };
  maxDate: { year: number; month: number; day: number; };
  timeZone: string;
  isLfp:boolean;

  constructor(private store: Store<AppState>,
    private route: ActivatedRoute,
    config: ConfigService,
    private valiadtor: ValidationService,
    private modalService: NgbModal) {
    super();
    this.isLfp = config.isLfp();
    this.isDebug = config.isDebug();
    this.timeZone = config.getTimezone();
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      if (params['id']) {
        const interviewId = params['id'];
        this.store.dispatch(new GetInterviewDetailsAction(interviewId));
        this.details$ = this.store.select(selectInterviewDetailsById(interviewId))
        // .pipe(
        //   map((state: InterviewsState) => state && state.details ? state.details[interviewId] : null)
        // );
      }
    });

    const userRole$ = this.store.select(selectCurrentUserRole());
    const combined$ = this.details$.pipe(
      mergeMap((details) => {
        const languages$ = this.store.select(selectLanguagesByIds(details?.languages.map(l => l.languageId)));
        const location$ = this.store.select(selectLocationById(details?.location.locationId));
        return combineLatest([of(details), languages$, location$, userRole$]);
      }));

    combined$.pipe(
      takeUntil(this.componentDestroyed))
      .subscribe(([details, languages, location, role]) => {
        this.isPastEvent = details.isPast;
        this.minDate = getMinDate(details.arrivalDate);
        this.maxDate = getMaxDate(details.arrivalDate);
        this.data = cloneDeep(details);
        this.data.languages = languages || [];
        this.data.location = location ? cloneDeep(location) : details.location;
        this.original = cloneDeep(this.data);
        switch (role) {
          case Roles.Admin:
          case Roles.League:
            this.interviewView = this.data.interviewee ? EInterviewView.LeagueUpdate : EInterviewView.LeagueCreate;
            this.lastSaved = this.getLastSaved();
            break;
          case Roles.PLPBroadcaster:
          case Roles.Broadcaster:
            const exists = this.data.requests && this.data.requests.length > 0;
            if (exists) {
              this.interviewView = EInterviewView.BroadcasterRead;
              this.broadcasterRequest = this.data.requests[0];
            } else {
              this.interviewView = this.isPastEvent ? EInterviewView.BroadcasterRead : EInterviewView.BroadcasterEdit;
              this.broadcasterRequest = InterviewRequestMapper.getEmptyRequest();
            }
            this.lastSaved = this.broadcasterRequest.lastSaved;
            break;
          default: this.interviewView = EInterviewView.Undefined;
        }
      });

    const dataLoading$ = this.store.select(selectInterviewDetailsLoading());
    const userLoading$ = this.store.select(selectUserIsLoading());
    const locationLoading$ = this.store.select(selectLocationLoading());
    const languagesLoading$ = this.store.select(selectLanguagesLoading());
    this.isLoading$ = combineLatest([dataLoading$, userLoading$, locationLoading$, languagesLoading$]).pipe(
      map(([d, u, l, lang]) => !!d && !!u && !!l && !!lang)
    );

    this.store.dispatch(new GetInterviewLocationsAction());
  }

  getLastSaved() {
    if (isGuidNotEmpty(this.data.interviewId)) {
      return getLastSavedText(this.data.updatedOnUtc, this.data.updatedBy, EventStatus[this.data.status]);
    } else {
      return 'To be saved';
    }
  }

  onLeagueCreate() {
    if (this.valiadtor.isInterviewValid(this.data)) {
      this.data.status = EventStatus.Submitted;
      this.store.dispatch(new CreateInterviewAction(this.data));
      this.interviewView = EInterviewView.LeagueUpdate;
    }
  }

  onLeagueEdit() {
    this.interviewView = EInterviewView.LeagueCreate;
  }

  onCancelCreate() {
    this.interviewView = EInterviewView.LeagueUpdate;
    this.data = cloneDeep(this.original);
  }

  onCancel() {
    this.data = cloneDeep(this.original);
  }

  // --- Requests
  private saveOrSubmitRequest(request: FxRequest, status: RequestStatus) {
    const model: FxRequest = request;
    if (this.isLfp && !this.valiadtor.isRequestValid(request)){
      return;
    }
    request.status = status;
    request.broadcasterNote.message = request.note;
    this.store.dispatch(new CreateUpdateInterviewRequestAction(model, this.data.interviewId));
    this.interviewView = EInterviewView.BroadcasterRead;
  }

  onSaveRequest(event: { identity: IDentity, request: FxRequest }) {
    this.saveOrSubmitRequest(event.request, RequestStatus.Draft);
  }

  onSubmitRequest(event: { identity: IDentity, request: FxRequest }) {
    this.saveOrSubmitRequest(event.request, RequestStatus.Submitted);
  }

  onApproveRejectRequest(event: { identity: IDentity, request: FxRequest }) {
    const request = event.request;
    request.leagueNote.message = event.request.note;
    if (request.status === RequestStatus.Reject) {
      if (isNonEmptyNotes(request.leagueNote)) {
        this.confirmedReject(request);
      } else {
        const modalRef = this.modalService.open(RejectConfirmComponent);
        const instance = modalRef.componentInstance as RejectConfirmComponent;
        return instance.submit
          .subscribe((result: boolean) => {
            if (result) {
              this.confirmedReject(request);
            } else {
              request.status = RequestStatus.Submitted;
            }
          });
      }
    } else {
      this.store.dispatch(new CreateUpdateInterviewRequestAction(event.request, this.data.interviewId));
    }
    return undefined;
  }

  confirmedReject(request) {
    this.store.dispatch(new CreateUpdateInterviewRequestAction(request, this.data.interviewId));
  }

  onWithdrawRequest(event: { identity: IDentity, request: FxRequest }) {

    const modalRef = this.modalService.open(ConfirmWithdrawPopupComponent, { centered: true });
    const instance = modalRef.componentInstance as ConfirmWithdrawPopupComponent;
    const s = instance.withdraw
      .subscribe(doWithdraw => {
        if (doWithdraw === true) {
          this.saveOrSubmitRequest(event.request, RequestStatus.Withdrawn);
          this.interviewView = EInterviewView.BroadcasterRead;
          s.unsubscribe();
        }
      });
  }

  changeLanguages(event) {
    this.data.languages = event;
  }

  onRequestEditMode() {
    this.interviewView = EInterviewView.BroadcasterEdit;
  }

  onCancelRequest() {
    this.interviewView = EInterviewView.BroadcasterRead;
    this.data.requests = cloneDeep(this.original.requests);
  }

  // Time picking
  onPickerInit() {
    this.initTimePickers(this.data.arrivalTime);
  }

  dateChanged(event: moment.Moment) {
    this.data.arrivalDate = moment.utc(event, 'YYYY-MM-DD HH:mm:ss')
      .hour(this.data.arrivalTime.hour)
      .minutes(this.data.arrivalTime.minute);
    this.data.startDate = moment.utc(event, 'YYYY-MM-DD HH:mm:ss')
      .hour(this.data.startTime.hour)
      .minutes(this.data.startTime.minute);
  }

  onArrivalTimeChanged(event: ITimePicker) {
    this.minEnd.next({
      hour: event.hour,
      minute: event.minute
    });
    this.data.arrivalDate = moment.utc(this.data.arrivalDate.hours(event.hour).minutes(event.minute), 'YYYY-MM-DD HH:mm:ss');
  }

  onStartTimeChanged(event: ITimePicker) {
    this.data.startDate = moment.utc(this.data.startDate.hours(event.hour).minutes(event.minute), 'YYYY-MM-DD HH:mm:ss');
  }

  initTimePickers(model?: ITimePicker) {
    this.minStart.next({
      hour: 0,
      minute: 0
    });
    this.maxStart.next({
      hour: 23,
      minute: 59
    });
    if (model && model.hour && model.minute) {
      this.minEnd.next({
        hour: model.hour,
        minute: model.minute
      });
    } else {
      this.minEnd.next({
        hour: 0,
        minute: 0
      });
    }
    this.maxEnd.next({
      hour: 23,
      minute: 59
    });
  }
}
